/**
 * Picking a source a correct reply should draw on, as a view of the case panel: the brand's SOPs,
 * running workflows and knowledge sources, searched by name, with what the AI last used first.
 */

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { DatasetExpectedSources } from '@/types/simulation-dataset.types';
import { SOURCE_KIND_LABEL, type SourceKind, type SourceOption } from './source-kinds';

const GROUPS: Array<{ kind: SourceKind; heading: string }> = [
	{ kind: 'sopIds', heading: 'SOPs' },
	{ kind: 'workflowIds', heading: 'Workflows' },
	{ kind: 'knowledgeIds', heading: 'Knowledge' }
];

interface SourcePickerProps {
	options: SourceOption[];
	/** What the AI last used on the case, offered first. */
	usedByRun: SourceOption[];
	chosen: DatasetExpectedSources;
	onPick: (option: SourceOption) => void;
}

export const SourcePicker = ({ options, usedByRun, chosen, onPick }: SourcePickerProps) => {
	const available = (option: SourceOption) => !chosen[option.kind].includes(option.id);
	const item = (option: SourceOption, prefix = '') => (
		<CommandItem
			key={`${prefix}${option.kind}:${option.id}`}
			value={`${prefix}${option.kind}:${option.id}`}
			keywords={[option.name, SOURCE_KIND_LABEL[option.kind]]}
			onSelect={() => onPick(option)}
			className='gap-2 py-2'
		>
			<span className='w-16 shrink-0 text-xs leading-4 text-gray-800'>{SOURCE_KIND_LABEL[option.kind]}</span>
			<span className='min-w-0 flex-1 truncate text-[13px] leading-4.5'>{option.name}</span>
		</CommandItem>
	);
	const used = usedByRun.filter(available);

	return (
		<Command className='flex min-h-0 flex-1 flex-col rounded-none'>
			<CommandInput placeholder='Search SOPs, workflows and knowledge' autoFocus />
			<CommandList className='max-h-none min-h-0 flex-1 border-t border-gray-500'>
				<CommandEmpty>Nothing matches.</CommandEmpty>
				{used.length > 0 && <CommandGroup heading='Used by the AI'>{used.map((option) => item(option, 'run|'))}</CommandGroup>}
				{GROUPS.map(({ kind, heading }) => {
					const list = options.filter((option) => option.kind === kind && available(option));
					return list.length > 0 ? (
						<CommandGroup key={kind} heading={heading}>
							{list.map((option) => item(option))}
						</CommandGroup>
					) : null;
				})}
			</CommandList>
		</Command>
	);
};
